import React, { useCallback, useState } from 'react'
import { Text } from 'react-native'
import Layout from '../../Auth/Layout'
import { observer } from 'mobx-react'
import SubmitButton from '../../Auth/SubmitButton'
import Title, { SubTitle } from '../../Auth/Title'
import { Link } from '../../../ui/atoms/Link'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { Spacer } from '../../../ui/atoms'
import LinkLabel from '../../Auth/LinkLabel'
import PasswordInput from '../../Auth/PasswordInput'
import { Trans, useTranslation } from 'react-i18next'
import { ErrorMessage } from '../../Auth/ErrorMessage'
import BackButton from '../../Auth/BackButton'
import { ErrorCode } from '@linqpal/models/src/types/exceptions'
import validatePassword from '../../Auth/validatePassword'

export type onNextPayload = { password: string }

interface SignInPasswordProps {
  onNext: (payload: onNextPayload) => void
  onBack: () => void
  onForgotPassword: () => void
  onSignUp: () => void
  login: string
  loading: boolean
  error?: ErrorCode | false
}

const SignInPassword: React.FC<SignInPasswordProps> = observer(
  ({ onNext, onForgotPassword, onBack, onSignUp, login, loading, error }) => {
    const [password, setPassword] = useState<string>('')
    const { t } = useTranslation(['auth'])

    const handleNext = useCallback(() => {
      onNext({ password })
    }, [onNext, password])

    const handleChange = useCallback(
      (text: string) => {
        setPassword(text)
      },
      [setPassword],
    )

    const isValidPassword: boolean = validatePassword(password).length
    return (
      <Layout onBackPress={onBack}>
        <BackButton testID="back-button-to-login" onPress={onBack} />
        <Title testID="password-title">{t('auth:signIn.passwordTitle')}</Title>
        <SubTitle style={{ marginTop: 16 }}>
          <Trans
            t={t}
            i18nKey={'auth:signIn.caption'}
            components={{ styled: <Text style={{ color: '#00A0F3' }} /> }}
            values={{ login }}
          />
        </SubTitle>
        <PasswordInput
          testID="password"
          label={t('auth:signIn.passwordLabel')}
          value={password}
          onChangeText={handleChange}
          newPassword
          showEye
          mandatory
        />
        <Spacer />
        <ErrorMessage error={error} />
        <Spacer height={40} />
        <LinkLabel
          testID="forgot-password"
          onPress={onForgotPassword}
          style={{ color: '#668598', fontSize: 14 }}
        >
          <Trans
            t={t}
            i18nKey={'auth:signIn.forgotLink'}
            components={{
              styled: (
                <Text
                  style={{ color: '#00A0F3' }}
                  testID="forgot_password_link"
                />
              ),
            }}
            values={{ login: 'Click Here' }}
          />
        </LinkLabel>
        <Spacer height={20} />
        <SubmitButton
          onPress={handleNext}
          disabled={!isValidPassword || loading}
          loading={loading}
          testID={'sign-in-submit-btn'}
        >
          {t('auth:signIn.loginButtonLabel')}
        </SubmitButton>
        <Spacer height={40} />
        <Text
          style={{
            fontSize: 16,
            fontWeight: '500',
            color: commonColors.textLight,
            alignSelf: 'center',
          }}
          testID="link-to-signup"
        >
          {`${t('auth:signIn.signUpQuestion')} `}
          <Link
            textStyle={{ fontSize: 16, fontWeight: '500' }}
            title={t('auth:SignUp')}
            color={commonColors.accentText}
            testID="signup_link"
            onPress={onSignUp}
          />
        </Text>
      </Layout>
    )
  },
)

export default SignInPassword
